import { type FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SmartphoneNfc, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/auth-store.js';
import { isPublicSignupEnabled } from '../lib/features.js';

export const Login: FC = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Why the previous session ended (e.g. agency suspended), shown once.
  const [notice] = useState<string | null>(() => {
    try {
      const value = sessionStorage.getItem('auth_notice');
      sessionStorage.removeItem('auth_notice');
      return value;
    } catch {
      return null;
    }
  });
  const { login, register, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  // Self-signup is closed unless built with VITE_PUBLIC_SIGNUP_ENABLED=true.
  const signupEnabled = isPublicSignupEnabled();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isRegister && signupEnabled) {
        await register(email, password, name, orgName);
      } else {
        await login(email, password);
      }
      navigate('/', { replace: true });
    } catch {
      // error is in store
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950">
      <div className="w-full max-w-sm space-y-6 px-4">
        {/* Logo */}
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600">
            <SmartphoneNfc className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-100">LaxmiAI</h1>
          <p className="mt-1 text-sm text-slate-500">Creator Operations Platform</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <div>
              <label htmlFor="name" className="mb-1.5 block text-xs font-medium text-slate-400">Name</label>
              <input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Your name"
                required={isRegister}
              />
            </div>
          )}
          {isRegister && (
            <div>
              <label htmlFor="org" className="mb-1.5 block text-xs font-medium text-slate-400">Organization</label>
              <input
                id="org"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Your org name"
              />
            </div>
          )}
          <div>
            <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-slate-400">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearError(); }}
              className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="you@example.com"
              required
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-slate-400">Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearError(); }}
                className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 pl-3 pr-9 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {notice !== null && error === null && (
            <div className="rounded-lg border border-amber-900/50 bg-amber-900/10 px-3 py-2 text-xs text-amber-400">
              {notice}
            </div>
          )}

          {error !== null && (
            <div className="rounded-lg border border-red-900/50 bg-red-900/10 px-3 py-2 text-xs text-red-400">
              {error}
            </div>
          )}

          {!isRegister && (
            <p className="text-right text-xs">
              <Link to="/forgot-password" className="text-slate-500 hover:text-slate-300">Forgot password?</Link>
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="flex h-9 w-full items-center justify-center rounded-lg bg-blue-600 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              isRegister ? 'Create Account' : 'Sign In'
            )}
          </button>
        </form>

        {signupEnabled ? (
          <p className="text-center text-xs text-slate-600">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              onClick={() => { setIsRegister(!isRegister); clearError(); }}
              className="font-medium text-blue-400 hover:text-blue-300"
            >
              {isRegister ? 'Sign in' : 'Register'}
            </button>
          </p>
        ) : (
          <p className="text-center text-xs text-slate-600">
            New agencies join by invitation. Ask the platform administrator for an invitation link.
          </p>
        )}

        <p className="text-center text-[11px] text-slate-600">
          By signing in you agree to the <Link to="/terms" className="hover:text-slate-400">Terms of Service</Link> and{' '}
          <Link to="/privacy" className="hover:text-slate-400">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
};
